import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import { Alert, Badge, Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Progress } from "reactstrap";
import Loading from "../../components/Loading";
import { callRemote, GET, POST } from "../../service/RemoteService";
import {
    SINGLE_IMAGES, changedSinceListingUpdate, clearGallery, errorMessage, kindOf, planRename, renameGalleryPhoto, splitName, listPhotos, missingForSubmit, photoUrl,
    gallerySizeProblems, planGalleryAdd, removeGalleryPhoto, singleImageProblem, updateListing, uploadEditorPhotos,
} from "../../service/PhotoUploadService";
import { shootsActions } from "../../store/storeIndex";
import { displayAlert } from "../../store/AlertSlice";
import { determineIfTokenError, isSessionError } from "../../store/AuthThunks";
import passengerWhiteExample from "../../assets/photo-placeholders/passenger-white.jpg";
import passengerTransparentExample from "../../assets/photo-placeholders/passenger-transparent.jpg";
import frontQuarterDriverExample from "../../assets/photo-placeholders/front-quarter-driver.jpg";
import bannerExample from "../../assets/photo-placeholders/banner.jpg";

const ACCEPT = "image/jpeg,image/png,image/webp,image/avif";
const thumb = { width: '100%', height: '110px', objectFit: 'cover', background: '#eee', display: 'block' };

// Shown, faded, in a slot that has nothing yet: an example of what that image should be. Contained
// rather than cropped, so the label along each example's bottom edge stays visible.
const EXAMPLES = {
    'passenger-white': passengerWhiteExample,
    'passenger-transparent': passengerTransparentExample,
    'front-quarter-driver': frontQuarterDriverExample,
    'banner': bannerExample,
};
const example = { ...thumb, objectFit: 'contain', background: '#f8f9fa', opacity: 0.5 };

// One editor job's photos, as they are in S3. The assigned editor builds the gallery here over as many
// sessions as they need - adding batches, replacing single images, removing photos - and submits from
// here, after which it is read-only to them. An editor manager can change the photos at any stage,
// before submission, in review or after approval, and pushes their changes to the live listing.
// Anyone else sees it read-only.
const JobPhotos = () => {
    const { id } = useParams();
    const history = useHistory();
    const dispatch = useDispatch();
    const { user, getAccessTokenSilently } = useAuth0();

    const [job, setJob] = useState(null);
    const [photos, setPhotos] = useState(null);
    const [progress, setProgress] = useState(null);
    const [error, setError] = useState(null);
    const [pendingBatch, setPendingBatch] = useState(null);
    const [confirmingRemove, setConfirmingRemove] = useState(null);
    // The photo being renamed and the stem typed so far; its extension is fixed.
    const [renaming, setRenaming] = useState(null);
    const [confirmingClear, setConfirmingClear] = useState(false);

    const fail = useCallback((e) => {
        if (isSessionError(e)) {
            dispatch(determineIfTokenError(e));
        }
        setError(errorMessage(e));
    }, [dispatch]);

    const refresh = useCallback(async () => {
        const [jobResponse, photosResponse] = await Promise.all([
            callRemote(getAccessTokenSilently, GET, `/jobs/${id}`),
            listPhotos(getAccessTokenSilently, id),
        ]);
        setJob(jobResponse);
        setPhotos(photosResponse);
    }, [getAccessTokenSilently, id]);

    useEffect(() => {
        refresh().catch(fail);
    }, [refresh, fail]);

    if (error && !photos) {
        return <Alert color="danger">{error}</Alert>;
    }
    if (!job || !photos) {
        return <Loading />;
    }

    const roles = (user && user["http://exoticcartrader.com/role"]) || [];
    const isManager = roles.includes('Editor Manager');
    const canSubmit = job.creatorId === user.sub && !job.submittedDate;
    const editable = isManager || canSubmit;
    const listingBehind = isManager ? changedSinceListingUpdate(photos, job) : null;
    const busy = progress !== null;
    const missing = missingForSubmit(photos);
    const imageByKind = Object.fromEntries(photos.images.map(photo => [kindOf(photo), photo]));

    // Runs one change, then shows what S3 has now - whatever of a batch landed is kept even if some failed.
    const change = async (label, work) => {
        setError(null);
        setProgress({ label, done: 0, total: 0 });
        try {
            await work();
        } catch (e) {
            fail(e);
        } finally {
            setProgress(null);
            await refresh().catch(fail);
        }
    };

    const upload = (images, gallery) => change(
        gallery.length > 0 ? `Uploading ${gallery.length} photos` : 'Uploading',
        () => uploadEditorPhotos(getAccessTokenSilently, id, images, gallery,
            (done, total) => setProgress(p => ({ ...p, done, total }))));

    const replaceSingle = (kind, file) => {
        const problem = file && singleImageProblem(kind, file);
        if (problem) {
            setError(problem);
        } else if (file) {
            upload({ [kind]: file }, []);
        }
    };

    const addGallery = async (files) => {
        setPendingBatch(null);
        setError(null);
        const plan = planGalleryAdd(photos.gallery, files);
        if (plan.problems.length === 0 && plan.files.length > 0) {
            setProgress({ label: 'Checking photo sizes', done: 0, total: plan.files.length });
            plan.problems.push(...await gallerySizeProblems(plan.files,
                (done, total) => setProgress(p => ({ ...p, done, total }))));
            setProgress(null);
        }
        if (plan.problems.length > 0) {
            setError(plan.problems.join('. '));
        } else if (plan.replacing.length > 0) {
            setPendingBatch(plan);   // confirm first: same-named photos are overwritten
        } else if (plan.files.length > 0) {
            upload({}, plan.files);
        }
    };

    const startRename = (name) => {
        setConfirmingRemove(null);
        setRenaming({ name, stem: splitName(name).stem });
    };

    const rename = () => {
        const { to, problem } = planRename(photos.gallery, renaming.name, renaming.stem);
        if (problem) {
            setError(problem);
            return;
        }
        const from = renaming.name;
        setRenaming(null);
        if (to !== from) {
            change(`Renaming ${from}`, () => renameGalleryPhoto(getAccessTokenSilently, id, from, to));
        }
    };

    const remove = (name) => {
        setConfirmingRemove(null);
        change(`Removing ${name}`, () => removeGalleryPhoto(getAccessTokenSilently, id, name));
    };

    // Only ever reached from the confirmation dialog.
    const clearAll = () => {
        setConfirmingClear(false);
        setConfirmingRemove(null);
        setRenaming(null);
        change(`Removing all ${photos.gallery.length} gallery photos`, async () => {
            const { removed } = await clearGallery(getAccessTokenSilently, id);
            dispatch(displayAlert(true, `Removed ${removed} gallery photos`));
        });
    };

    // Submitting is what calls ECT's photos-complete. If the API refuses - something missing, or ECT
    // not confirming the gallery - the job stays open here with the reason shown.
    const submit = async () => {
        setError(null);
        setProgress({ label: 'Submitting', done: 0, total: 0 });
        try {
            await callRemote(getAccessTokenSilently, POST, `/jobs/${id}/submit`, {});
        } catch (e) {
            setProgress(null);
            fail(e);
            return;
        }
        dispatch(shootsActions.incrementActionsTaken());
        dispatch(displayAlert(true, "Photos submitted for review"));
        history.push('/my-jobs');
    };

    // A manager's changes are in S3 already; this makes a live vehicle page show them rather than its cache.
    const pushToListing = () => change('Updating the listing', async () => {
        const updated = await updateListing(getAccessTokenSilently, id);
        dispatch(displayAlert(true, `Listing updated with ${updated.galleryPhotoCount} gallery photos`));
    });

    // Resets the input, so choosing the same file again still fires onChange.
    const picked = (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = '';
        return files;
    };

    return (
        <div className="mb-5">
            <h2>{job.car}</h2>
            <p className="text-muted">{job.vin} {job.lotNumber ? `· ${job.lotNumber}` : ''}</p>

            {!editable ? (
                <Alert color="info">
                    {job.submittedDate ? 'Submitted - these photos are with the manager for review.' : 'Viewing only.'}
                </Alert>
            ) : null}
            {isManager && !canSubmit ? (
                <Alert color={listingBehind ? 'warning' : 'secondary'}>
                    {job.approvalDate ? 'Approved. ' : job.submittedDate ? 'Submitted for review. ' : 'The editor is still working on this. '}
                    As an editor manager you can change these photos at any time. Changes land straight away,
                    but a live vehicle page keeps showing its cached photos until you update the listing.
                    {job.photosCompletedDate ? (
                        <div className="mt-2">
                            {listingBehind
                                ? <strong>Photos have changed since the listing was last updated. </strong>
                                : <span>Last updated {new Date(job.photosCompletedDate).toLocaleString()}. </span>}
                            <Button size="sm" color={listingBehind ? 'primary' : 'secondary'} disabled={busy || missing.length > 0}
                                    onClick={pushToListing}>Update listing</Button>
                        </div>
                    ) : null}
                </Alert>
            ) : null}
            {error ? <Alert color="danger" toggle={() => setError(null)}>{error}</Alert> : null}
            {progress ? (
                <div className="mb-3">
                    <div>{progress.label}...</div>
                    {progress.total > 0 ? (
                        <Progress value={progress.done} max={progress.total}>{progress.done} / {progress.total}</Progress>
                    ) : <Progress animated value={100} />}
                </div>
            ) : null}

            <h4 className="mt-4">Single images</h4>
            <div className="row">
                {SINGLE_IMAGES.map(image => {
                    const photo = imageByKind[image.kind];
                    return (
                        <div className="col-6 col-md-3 mb-3" key={image.kind}>
                            {photo
                                ? <a href={photoUrl(photo)} target="_blank" rel="noopener noreferrer"><img src={photoUrl(photo)} alt={image.label} style={thumb} /></a>
                                : (
                                    <div style={{ position: 'relative' }}>
                                        <img src={EXAMPLES[image.kind]} alt={`Example: ${image.label}`} style={example} />
                                        <Badge color="warning" style={{ position: 'absolute', top: '8px', left: '8px' }}>Missing</Badge>
                                    </div>
                                )}
                            <small className="d-block mt-1">{image.label}</small>
                            {editable ? (
                                <Label className="btn btn-sm btn-outline-primary mt-1 mb-0">
                                    {photo ? 'Replace' : 'Upload'}
                                    <Input type="file" hidden disabled={busy}
                                           accept={image.only || ACCEPT}
                                           onChange={e => replaceSingle(image.kind, picked(e)[0])} />
                                </Label>
                            ) : null}
                        </div>
                    );
                })}
            </div>

            <h4 className="mt-4">Gallery <small className="text-muted">({photos.gallery.length} photos, shown in filename order)</small></h4>
            {editable ? (
                <div className="d-flex align-items-start">
                    <Label className="btn btn-primary">
                        Add photos
                        <Input type="file" accept={ACCEPT} multiple hidden disabled={busy} onChange={e => addGallery(picked(e))} />
                    </Label>
                    {photos.gallery.length > 0 ? (
                        <Button outline color="danger" className="ml-2" disabled={busy}
                                onClick={() => setConfirmingClear(true)}>Remove all photos</Button>
                    ) : null}
                </div>
            ) : null}
            {pendingBatch ? (
                <Alert color="warning">
                    {pendingBatch.replacing.length} of these {pendingBatch.files.length} photos have the same name as one
                    already in the gallery and will replace it: {pendingBatch.replacing.slice(0, 5).join(', ')}
                    {pendingBatch.replacing.length > 5 ? ', ...' : ''}
                    <div className="mt-2">
                        <Button size="sm" color="primary" onClick={() => { const plan = pendingBatch; setPendingBatch(null); upload({}, plan.files); }}>Upload and replace</Button>{' '}
                        <Button size="sm" onClick={() => setPendingBatch(null)}>Cancel</Button>
                    </div>
                </Alert>
            ) : null}
            <div className="row mt-2">
                {photos.gallery.map(photo => (
                    <div className="col-6 col-sm-4 col-md-3 col-lg-2 mb-3" key={photo.name}>
                        <a href={photoUrl(photo)} target="_blank" rel="noopener noreferrer">
                            <img src={photoUrl(photo)} alt={photo.name} loading="lazy" style={thumb} />
                        </a>
                        {renaming && renaming.name === photo.name ? (
                            <div className="mt-1">
                                <div className="d-flex align-items-center">
                                    <Input bsSize="sm" autoFocus value={renaming.stem} aria-label={`New name for ${photo.name}`}
                                           onChange={e => setRenaming({ ...renaming, stem: e.target.value })}
                                           onKeyDown={e => { if (e.key === 'Enter') rename(); if (e.key === 'Escape') setRenaming(null); }} />
                                    <small className="ml-1">{splitName(photo.name).extension}</small>
                                </div>
                                <Button size="sm" color="primary" className="mt-1" disabled={busy} onClick={rename}>Save</Button>{' '}
                                <Button size="sm" className="mt-1" onClick={() => setRenaming(null)}>Cancel</Button>
                            </div>
                        ) : (
                            // Never truncated: the end of the name is what decides the gallery's order.
                            <small className="d-block" style={{ wordBreak: 'break-all' }}>{photo.name}</small>
                        )}
                        {editable && !(renaming && renaming.name === photo.name) ? (confirmingRemove === photo.name
                            ? <span><Button size="sm" color="danger" disabled={busy} onClick={() => remove(photo.name)}>Remove</Button>{' '}
                                <Button size="sm" onClick={() => setConfirmingRemove(null)}>Keep</Button></span>
                            : <span>
                                <Button size="sm" outline color="secondary" disabled={busy} onClick={() => startRename(photo.name)}>Rename</Button>{' '}
                                <Button size="sm" outline color="danger" disabled={busy} onClick={() => setConfirmingRemove(photo.name)}>Remove</Button>
                              </span>
                        ) : null}
                    </div>
                ))}
            </div>

            {canSubmit ? (
                <div className="mt-4">
                    {missing.length > 0 ? <p className="text-muted">Still needed before you can submit: {missing.join(', ')}</p> : null}
                    <Button color="success" size="lg" disabled={busy || missing.length > 0} onClick={submit}>Submit for review</Button>
                </div>
            ) : null}

            {/* Clearing deletes every gallery photo from S3 at once, so it is never one click: the
                dialog says how many, that there is no undo, and - once the photos have gone to the
                listing - that a live vehicle page will show no gallery until new ones are up. */}
            <Modal isOpen={confirmingClear} toggle={() => setConfirmingClear(false)}>
                <ModalHeader toggle={() => setConfirmingClear(false)}>Remove all gallery photos?</ModalHeader>
                <ModalBody>
                    <p>
                        This deletes all <strong>{photos.gallery.length}</strong> gallery photos for {job.car}.
                        They cannot be recovered - they would have to be uploaded again.
                    </p>
                    <p className="mb-0">The four single images (thumbnail, cutout, front quarter and banner) are not affected.</p>
                    {job.photosCompletedDate ? (
                        <Alert color="warning" className="mt-3 mb-0">
                            These photos are already on the listing. If it is live, its vehicle page will show no
                            gallery until new photos are uploaded and {canSubmit ? 'the job is submitted' : 'the listing is updated'}.
                        </Alert>
                    ) : null}
                </ModalBody>
                <ModalFooter>
                    <Button color="danger" onClick={clearAll}>Remove all {photos.gallery.length} photos</Button>
                    <Button autoFocus onClick={() => setConfirmingClear(false)}>Cancel</Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default withAuthenticationRequired(JobPhotos, {
    onRedirecting: () => <Loading />,
});
